import { useQueries, useQuery } from "@tanstack/react-query";
import { getArtist, getArtistPoints, getArtistPrice, ApiError } from "../api";
import { Artist, ArtistPointsResponse, ArtistPrice } from "../api/types";

const ONE_HOUR_MS = 60 * 60 * 1000;

export const artistKeys = {
  all: ["artist"] as const,
  detail: (artistId: string) => [...artistKeys.all, artistId] as const,
  price: (artistId: string, draftId?: string) =>
    [...artistKeys.all, artistId, "price", draftId] as const,
  points: (artistId: string, startDate?: string, endDate?: string) =>
    [...artistKeys.all, artistId, "points", startDate, endDate] as const,
};

export function useGetArtist(artistId: string | undefined) {
  return useQuery<Artist, ApiError>({
    queryKey: artistKeys.detail(artistId ?? ""),
    queryFn: () => getArtist(artistId as string),
    enabled: !!artistId,
    staleTime: ONE_HOUR_MS,
  });
}

export function useGetArtistPrice(
  artistId: string | undefined,
  draftId: string | undefined,
) {
  return useQuery<ArtistPrice, ApiError>({
    queryKey: artistKeys.price(artistId ?? "", draftId),
    queryFn: () => getArtistPrice(artistId as string, draftId as string),
    enabled: !!artistId && !!draftId,
    staleTime: ONE_HOUR_MS,
  });
}

export function useGetArtistPoints(
  artistId: string | undefined,
  params: { startDate?: string; endDate?: string },
) {
  return useQuery<ArtistPointsResponse, ApiError>({
    queryKey: artistKeys.points(
      artistId ?? "",
      params.startDate,
      params.endDate,
    ),
    queryFn: () =>
      getArtistPoints(
        artistId as string,
        params as { startDate: string; endDate?: string },
      ),
    enabled: !!artistId,
    staleTime: ONE_HOUR_MS,
  });
}

export function useGetArtists(artistIds: string[]) {
  return useQueries({
    queries: artistIds.map((id) => ({
      queryKey: artistKeys.detail(id),
      queryFn: () => getArtist(id),
      staleTime: ONE_HOUR_MS,
    })),
  });
}

export function useGetArtistPrices(
  artistIds: string[],
  draftId: string | undefined,
) {
  return useQueries({
    queries: artistIds.map((id) => ({
      queryKey: artistKeys.price(id, draftId),
      queryFn: () => getArtistPrice(id, draftId as string),
      enabled: !!draftId,
      staleTime: ONE_HOUR_MS,
    })),
  });
}

export function useGetArtistsPoints(
  artistIds: string[],
  params: { startDate?: string; endDate?: string },
) {
  return useQueries({
    queries: artistIds.map((id) => ({
      queryKey: artistKeys.points(id, params.startDate, params.endDate),
      queryFn: () => getArtistPoints(id, params),
      staleTime: ONE_HOUR_MS,
    })),
  });
}
